import { useEffect, useState } from 'react';
import type { ManagerRole } from '../../api/managerApi';
import { filterRoutesByRole } from '../../auth/permissions';
import SidebarSection from './SidebarSection';
import type { SidebarMenuSection } from './sidebarTypes';
import { useEventContext } from '../../contexts/EventContext';
import { APP_ROUTES, buildEventScopedPath } from '../../routes/appRoutes';

function createMenuSections(role: ManagerRole | null): SidebarMenuSection[] {
  const visibleRoutes = filterRoutesByRole(APP_ROUTES, role);

  return [
    {
      title: '대회',
      icon: 'flag',
      items: visibleRoutes.filter((route) => route.section === 'competition').map((route) => ({
        label: route.label,
        path: route.path,
      })),
    },
    {
      title: '통계',
      icon: 'statistic',
      items: visibleRoutes.filter((route) => route.section === 'statistics').map((route) => ({
        label: route.label,
        path: route.path,
      })),
    },
    {
      title: '결과 조회',
      icon: 'result',
      items: visibleRoutes.filter((route) => route.section === 'results').map((route) => ({
        label: route.label,
        path: route.path,
      })),
    },
  ];
}

type SidebarProps = Readonly<{
  activePath: string;
  role: ManagerRole | null;
}>;

function Sidebar({ activePath, role }: SidebarProps) {
  const { selectedEventId } = useEventContext();
  const menuSections = createMenuSections(role);
  const scopedMenuSections = menuSections.map((section) => ({
    ...section,
    items: section.items.map((item) => ({
      ...item,
      path: buildEventScopedPath(item.path, selectedEventId),
    })),
  }));
  const [searchKeyword, setSearchKeyword] = useState('');
  const normalizedSearchKeyword = searchKeyword.trim().toLowerCase();
  const filteredMenuSections = normalizedSearchKeyword
    ? scopedMenuSections
        .map((section) => ({
          ...section,
          items: section.items.filter((item) =>
            item.label.toLowerCase().includes(normalizedSearchKeyword)
          ),
        }))
        .filter((section) => section.items.length > 0)
    : scopedMenuSections;

  const activeSectionTitle =
    menuSections.find((section) => section.items.some((item) => item.path === activePath))?.title;

  const [openSections, setOpenSections] = useState<string[]>(() => {
    return activeSectionTitle ? [activeSectionTitle] : [];
  });

  useEffect(() => {
    if (!activeSectionTitle) {
      return;
    }

    setOpenSections((current) =>
      current.includes(activeSectionTitle) ? current : [...current, activeSectionTitle]
    );
  }, [activePath, activeSectionTitle]);

  const handleToggleSection = (title: string) => {
    setOpenSections((current) =>
      current.includes(title)
        ? current.filter((sectionTitle) => sectionTitle !== title)
        : [...current, title]
    );
  };

  return (
    <aside className="sidebar" aria-label="주 메뉴">
      <label className="search-row">
        <span className="plus">+</span>
        <input
          className="search-input"
          type="text"
          aria-label="메뉴 검색"
          placeholder="탐색"
          value={searchKeyword}
          onChange={(event) => setSearchKeyword(event.target.value)}
        />
        <span className="shortcut">⌘</span>
      </label>

      <nav className="nav-list">
        {filteredMenuSections.map((section) => (
          <SidebarSection
            activePath={activePath}
            expanded={Boolean(normalizedSearchKeyword) || openSections.includes(section.title)}
            onToggle={() => handleToggleSection(section.title)}
            section={section}
            key={section.title}
          />
        ))}
        {filteredMenuSections.length === 0 && (
          <p className="empty-search-result">검색 결과가 없습니다.</p>
        )}
      </nav>
    </aside>
  );
}

export default Sidebar;
